"use client";

import { Dialog, DialogContent } from "@/components/atoms/Dialog";
import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { markPromotionalBannerSeen } from "@/sessionStorage";
import { setPromotionalBannerOpen } from "@/store/slices/globalslice";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import type { RootState } from "@/store/store";
import { CLOSE_BTN_CATEGORY } from "@/types/common";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import OfferBox from "../atoms/OfferBox";
import PromotionalBanner from "./PromotionalBanner";

/* Header widgets (Header5/Header7) render their own fixed WhatsApp/phone FAB
   column on mobile, tagged with this attribute. It is a sibling fixed element,
   not part of this column, so anchoring both independently made the promo icon
   sit *on top of* the WhatsApp icon. Measure that dock and stack above it. */
const MOBILE_FLOAT_DOCK_SELECTOR = "[data-mobile-float-dock]";
const MOBILE_FLOAT_DOCK_GAP = 12;
/* Sanity bounds for the dock-derived anchor.
 *
 * `bottom` is computed from `window.innerHeight - rect.top`, which mixes two viewport
 * models: on iOS Safari / Chrome Android `getBoundingClientRect()` is relative to the
 * LAYOUT viewport while `innerHeight` follows the VISUAL one as the URL bar collapses
 * and expands. While they disagree the result is too small and the icon slides toward —
 * and on some devices past — the bottom edge. A measurement outside these bounds is
 * therefore treated as unusable: we drop back to the CSS fallback position (which is
 * safe-area aware) instead of pinning the icon somewhere it cannot be seen. */
const MIN_DOCK_ANCHOR_BOTTOM = 16;
/* Leave room for the icon itself so a runaway measurement cannot push it off the top. */
const DOCK_ANCHOR_TOP_MARGIN = 96;
/* Header widgets are dynamic chunks, so the dock can mount after this one. */
const DOCK_LOOKUP_INTERVAL = 250;
const DOCK_LOOKUP_ATTEMPTS = 20;

type DockAnchor = { left: number; bottom: number };

const useMobileFloatDockAnchor = () => {
	const [anchor, setAnchor] = useState<DockAnchor | null>(null);
	/* This component lives in the persistent `(root)` layout, but the dock belongs
	   to the page's header widget — a dynamic chunk that is destroyed and rebuilt
	   on every client-side navigation. Keyed on `pathname`, the scan restarts and
	   re-binds to the NEW dock node; without it the hook held a detached element,
	   fell back to the desktop anchor, and the promo icon landed on top of the
	   WhatsApp/phone column until a full reload. */
	const pathname = usePathname();

	useEffect(() => {
		let dock: HTMLElement | null = null;
		let resizeObserver: ResizeObserver | null = null;
		let scan: ReturnType<typeof setInterval> | null = null;

		const release = () => {
			resizeObserver?.disconnect();
			resizeObserver = null;
			dock = null;
		};

		const measure = () => {
			/* No dock on this page (header variant without one), or the node was
			   removed — drop any stale anchor so the column returns to its own
			   fixed position instead of pointing at where the old dock used to be. */
			if (!dock?.isConnected) {
				setAnchor(null);
				return;
			}
			const rect = dock.getBoundingClientRect();
			/* Hidden above 1024px (`min-[1024px]:hidden`) — fall back to the
			   desktop anchor instead of pinning to a zero-sized rect. */
			if (!rect.height) {
				setAnchor(null);
				return;
			}
			const bottom =
				window.innerHeight - rect.top + MOBILE_FLOAT_DOCK_GAP;
			/* Reject an implausible measurement rather than trusting it — see the
			   bounds comment above. Returning to the CSS fallback keeps the icon on
			   screen, and the next resize/observer tick re-measures, so this
			   self-heals once the two viewport models agree again. */
			if (
				!Number.isFinite(bottom) ||
				bottom < MIN_DOCK_ANCHOR_BOTTOM ||
				bottom > window.innerHeight - DOCK_ANCHOR_TOP_MARGIN
			) {
				setAnchor(null);
				return;
			}
			setAnchor({
				left: rect.left + rect.width / 2,
				bottom,
			});
		};

		/* Re-queries every tick rather than binding once: during a route change the
		   outgoing page's dock can still be mounted, so the first match is often the
		   node that is about to be thrown away. */
		const sync = () => {
			const el = document.querySelector<HTMLElement>(
				MOBILE_FLOAT_DOCK_SELECTOR
			);
			if (el !== dock) {
				release();
				if (el) {
					dock = el;
					/* Header5's dock grows when its contact panel expands. */
					resizeObserver = new ResizeObserver(measure);
					resizeObserver.observe(el);
				}
			}
			measure();
			return Boolean(dock);
		};

		sync();

		/* Header widgets are dynamic chunks, so the dock can mount after this one.
		   The window is bounded — once it closes, the ResizeObserver and the resize
		   listener keep the anchor current until the next navigation. */
		let attempts = 0;
		scan = setInterval(() => {
			attempts += 1;
			if (sync() || attempts >= DOCK_LOOKUP_ATTEMPTS) {
				if (scan) clearInterval(scan);
				scan = null;
			}
		}, DOCK_LOOKUP_INTERVAL);

		window.addEventListener("resize", measure);
		return () => {
			if (scan) clearInterval(scan);
			release();
			window.removeEventListener("resize", measure);
		};
	}, [pathname]);

	return anchor;
};

const FloatingIcons = () => {
	const dispatch = useDispatch();
	const dockAnchor = useMobileFloatDockAnchor();
	const isMobile = useIsMobile(768);
	const promotionalBanner = useSelector(
		(state: RootState) => state.globalslice.promotional_banner
	);
	const isPromotionalBannerOpen = useSelector(
		(state: RootState) => state.globalslice.isPromotionalBannerOpen
	);

	const floatingIconsVisibility = useSelector(
		(state: RootState) => state.globalslice.floatingIconsVisibility
	);

	const {
		isWhatsAppIconEnabled,
		isPhoneIconEnabled,
		isPromotionalBannerEnabled,
		isPromotionalFloatingIconEnabled,
		isPromotionalIconBlinkEnabled,
	} = floatingIconsVisibility;

	const floatingIcons = useSelector(
		(state: RootState) => state.globalslice.floatingIcons
	);

	const { whatsApp, phone } = floatingIcons;

	const handleOfferBoxClick = () => {
		if (promotionalBanner?.enabled) {
			dispatch(setPromotionalBannerOpen(true));
		}
	};

	const handleBannerClick = () => {
		markPromotionalBannerSeen();
		dispatch(setPromotionalBannerOpen(false));
		dispatch(
			handleOpenLeaddrop({
				variant: LEAD_DROP_VARIANTS.REGISTER,
			})
		);
	};

	const handleModalClose = (open: boolean) => {
		/* Any user-driven close (X button, Esc) counts as "seen": from here on the
		   banner stays behind the floating icon and never auto-pops again for this
		   session, while the icon can still open/close it as often as they like. */
		if (!open) {
			markPromotionalBannerSeen();
		}
		dispatch(setPromotionalBannerOpen(open));
	};

	const bannerImage =
		isMobile && promotionalBanner?.mobile_image
			? promotionalBanner.mobile_image
			: promotionalBanner?.desktop_image;

	return (
		<>
			<div
				className={cn(
					"fixed z-40 flex flex-col items-center",
					// HORIZONTAL is the only inset that differs on mobile. The 50px
					// desktop offset left the icon well inside the content on a ~390px
					// viewport, reading as "floating in the middle of the page" rather
					// than docked to a corner.
					"gap-3 left-4",
					// From 768px (= the useIsMobile threshold) restore the desktop
					// placement, which matches live CU's floating promo button.
					"tab:gap-4 tab:left-[50px]",
					// VERTICAL stays at 50px on every viewport. A tighter mobile inset
					// (16px was tried) tucks the icon into the browser's bottom chrome —
					// the collapsing URL/tab bar on iOS Safari and Chrome Android overlays
					// fixed content, so the icon vanished on handsets that have it and
					// looked fine on those that do not.
					//
					// env() is additive future-proofing only: this app does not set
					// `viewportFit: "cover"` (see app/layout.tsx), so iOS already insets
					// the layout viewport and the value resolves to 0px today. It starts
					// contributing automatically if cover is ever turned on.
					"bottom-[calc(50px+env(safe-area-inset-bottom))]"
				)}
				/* Mobile: sit directly above the header widget's WhatsApp/phone dock so
				   the icons read as one column instead of overlapping. */
				style={
					dockAnchor
						? {
							left: dockAnchor.left,
							bottom: dockAnchor.bottom,
							transform: "translateX(-50%)",
						}
						: undefined
				}
			>
				{/* Page-level switch for THIS icon only, subordinate to the page's promotional
				    banner switch above: with the banner off there is nothing to open, so the
				    icon stays hidden either way. Off hides the icon without disabling the
				    banner — it can still auto-pop once per session, the user just cannot
				    summon it back. NULL/true → on (resolved in NewPage). */}
				{Boolean(isPromotionalBannerEnabled) &&
					Boolean(isPromotionalFloatingIconEnabled) &&
					!isPromotionalBannerOpen &&
					promotionalBanner?.enabled &&
					bannerImage && (
						<OfferBox
							onClick={handleOfferBoxClick}
							promotionalIcon={promotionalBanner?.promotionalIcon}
							animationColor={promotionalBanner?.animationColor}
							blinkIntervalSeconds={promotionalBanner?.blinkIntervalSeconds}
							/* Page-level: off keeps the icon exactly where it is, just static. */
							blinkEnabled={Boolean(isPromotionalIconBlinkEnabled)}
						/>
					)}
				{Boolean(isWhatsAppIconEnabled) &&
					whatsApp?.whatsAppLink &&
					whatsApp?.whatsAppIcon &&
					isMobile && (
						<Link href={whatsApp?.whatsAppLink} target="_blank">
							<Image
								src={whatsApp?.whatsAppIcon}
								alt="WhatsApp"
								width={54}
								height={54}
							/>
						</Link>
					)}

				{Boolean(isPhoneIconEnabled) &&
					phone?.phoneLink &&
					phone?.phoneIcon &&
					isMobile && (
						<Link href={`tel:${phone?.phoneLink}`} target="_blank">
							<Image
								src={phone?.phoneIcon}
								alt="Phone"
								width={54}
								height={54}
							/>
						</Link>
					)}
			</div>

			{Boolean(isPromotionalBannerEnabled) &&
				promotionalBanner?.enabled &&
				bannerImage && (
					<Dialog
						open={isPromotionalBannerOpen}
						onOpenChange={handleModalClose}
					>
						<DialogContent
							className={cn(
								"p-0 border-0 bg-transparent shadow-none max-w-none",
								"xs:w-full xs:max-w-[calc(100vw-3rem)]",
								"sm:w-fit sm:h-fit sm:max-w-[calc(100vw-2rem)]"
							)}
							dialogOverlayStyle={cn("bg-black/50")}
							closeBtnType={CLOSE_BTN_CATEGORY.PROMOTIONAL_POPUP}
							onInteractOutside={(e) => e.preventDefault()}
						>
							<PromotionalBanner
								bannerImage={bannerImage}
								onBannerClick={handleBannerClick}
							/>
						</DialogContent>
					</Dialog>
				)}
		</>
	);
};

export default FloatingIcons;
